import React, { useContext, useEffect, useState } from 'react'
import { useParams, useHistory as useRouterHistory } from 'react-router-dom'
import { Context as AuthContext } from '../contexts/AuthContext'
import { useAccountingBook, useAccountingBookSummary } from '../hooks'
import { Loading, PageHeader, Card, EmptyResult, TransactionItem, ConfirmBox } from '../components'
import { userImageUrls } from '../constants'
import formatAmount from '../utilities/formatAmount'
import styles from './MemberPaymentsPage.module.scss'

const pageStyles = {
  bg: {
    width: '100%',
    height: 'calc(100vh)',
    maxHeight: '-webkit-fill-available',
    overflow: 'hidden',
    display: 'flex',
    flexFlow: 'column',
  },
}

// All payments involving one member (as payer OR as an ower), plus their balance.
const MemberPaymentsPage = () => {
  const { state: authState } = useContext(AuthContext)
  const { group_id, accounting_book_id, member_id } = useParams()
  const routerHistory = useRouterHistory()
  // Return to where we came from (overview card or 成員列表); fall back to overview.
  const handleBack = () => {
    if (window.history.length > 2) { routerHistory.goBack() }
    else { routerHistory.push(`/liff_entry/groups/${group_id}/accounting_books/${accounting_book_id}/overview`) }
  }
  // eslint-disable-next-line
  const [users, accountingBookDetails] = useAccountingBook(authState)
  // eslint-disable-next-line
  const [summary, sumLoading, _e, getSummary, _cud, settlements] = useAccountingBookSummary(authState)
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [deleteActive, setDeleteActive] = useState(null)

  const loadPayments = (cb) => {
    authState.api.getPayments(group_id, accounting_book_id, '')
      .then((res) => { setPayments(res.data.payments || []); setLoading(false); if (cb) { cb() } })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    if (authState && authState.api) { loadPayments() }
    // eslint-disable-next-line
  }, [authState])

  const handleDeletePayment = (payment) => setDeleteActive(payment)
  const deletePayment = (payment) => {
    authState.api.deletePayments(group_id, accounting_book_id, {
      payment_ids: [payment.id],
      builder_id: authState.userLineIdToken,
    }).then(() => {
      loadPayments()
      if (getSummary) { getSummary() }
      setDeleteActive(null)
    }).catch(() => setDeleteActive(null))
  }

  const symbol = accountingBookDetails.currency_symbol || '$'
  const exponent = accountingBookDetails.exponent || 0
  const settlement = (settlements || []).find((s) => s.user_id === member_id) || {}
  const user = (users || []).find((u) => u.id === member_id) || {}
  const name = settlement.user_display_name || user.displayName || '成員'
  const avatar = settlement.image_url || user.imageURL || userImageUrls[0]
  const debt = Number(settlement.debt) || 0

  const memberPayments = (payments || []).filter((p) =>
    p.payer_id === member_id || (p.allocations || []).some((a) => a.ower_id === member_id)
  )

  return (
    <div style={pageStyles.bg}>
      <PageHeader
        faicon='faChevronLeft'
        onBack={handleBack}
      >
        <span className={styles.headWrap}>
          <img className={styles.headAvatar} src={avatar} alt="" />
          {name}
        </span>
      </PageHeader>

      {(loading || sumLoading) ? <Loading /> : (
        <div className={styles.scroll}>
          {/* 三種狀態：付錢方（大家還要還你）／收錢方（你還要還大家）／結清 */}
          <Card className={styles.balanceCard}>
            {debt === 0 ? (
              <div className={styles.bcStatus}>
                <div className={styles.bcCheck}>✓</div>
                <div className={styles.bcSettled}>已經結清囉！</div>
              </div>
            ) : (
              <div className={styles.bcStatus}>
                <span className={debt > 0 ? styles.badgeCredit : styles.badgeDebit}>
                  {debt > 0 ? '大家還要還你' : '你還要還大家'}
                </span>
                <div className={`${styles.bcBigAmount} ${debt > 0 ? styles.amtCredit : styles.amtDebit}`}>
                  {formatAmount(Math.abs(debt), exponent, symbol)}
                </div>
              </div>
            )}
            <div className={styles.bcDivider} />
            <div className={styles.bcSpendRow}>
              <span className={styles.bcSpendLabel}>總花費</span>
              <span className={styles.bcSpendValue}>{formatAmount(settlement.acc || 0, exponent, symbol)}</span>
            </div>
          </Card>

          <div className={styles.sectionTitle}>相關帳款 ({memberPayments.length})</div>
          {memberPayments.length > 0 ? (
            <div className={styles.labelList}>
              {memberPayments.map((p) => (
                <TransactionItem
                  key={p.id}
                  object={p}
                  editMode={false}
                  selectedPaymentIds={[]}
                  changed={() => {}}
                  deleted={handleDeletePayment}
                  {...accountingBookDetails}
                />
              ))}
            </div>
          ) : <EmptyResult message='沒有相關帳款' />}
        </div>
      )}

      {deleteActive ? (
        <ConfirmBox
          title="刪除帳款"
          confirmed={() => deletePayment(deleteActive)}
          canceled={() => setDeleteActive(null)}
          confirm_text="確認"
          cancel_text="取消">
          <div style={{ paddingBottom: '20px' }}> 確認刪除 {deleteActive.description} 這筆帳款嗎? </div>
        </ConfirmBox>
      ) : null}
    </div>
  )
}

export default MemberPaymentsPage
